import { useEffect, useState } from "react";
import { apiBaseUrl, fetchCmsPage } from "../lib/cmsApi";
import { resolveCmsImagesInContent } from "../lib/cmsAssets";

/**
 * CMS page loader with anti-flicker behavior:
 * - Does NOT render fallback first.
 * - Waits for API attempt to finish.
 * - Uses CMS data when available; otherwise uses provided fallback.
 */
export function useCmsPage(pageKey, fallbackSections) {
  const safeFallback = fallbackSections && typeof fallbackSections === "object" ? fallbackSections : {};

  // Initialize state synchronously with fallback sections to render instantly
  const [state, setState] = useState(() => {
    const fallback = {};
    for (const [key, content] of Object.entries(safeFallback)) {
      fallback[key] = resolveCmsImagesInContent(content, apiBaseUrl);
    }
    return {
      status: "ready",
      sections: fallback,
      content: fallback,
      source: "fallback",
    };
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!pageKey) return;
      try {
        const data = await fetchCmsPage(pageKey);
        const apiSections = data?.sections || {};
        const hasContent = Object.keys(apiSections).length > 0;

        if (!cancelled && hasContent) {
          const merged = {};
          const currentFallback = fallbackSections && typeof fallbackSections === "object" ? fallbackSections : {};
          // Keep fallback sections for any keys not yet in CMS (new sections).
          for (const [key, content] of Object.entries(currentFallback)) {
            merged[key] = resolveCmsImagesInContent(content, apiBaseUrl);
          }
          for (const [key, section] of Object.entries(apiSections)) {
            merged[key] = resolveCmsImagesInContent(section.content, apiBaseUrl);
          }
          setState({ status: "ready", sections: merged, content: merged, source: "cms" });
        }
      } catch {
        // API failed — keep showing the fallback content
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [pageKey, fallbackSections]);

  return state;
}

